/** The two synthetic result lines a balance sheet carries in equity. */
export const BALANCE_SHEET_RESULT_KINDS = ["prior_result", "year_result"];
/** English fallback names of the result lines (the document translates by `kind`). */
const RESULT_LINE_NAMES = {
    prior_result: "Unappropriated result of prior years",
    year_result: "Result for the financial year",
};
/**
 * The categories whose normal balance is DEBIT (the rest — Liabilities,
 * Equity, Revenue — are credit-normal). Category names as stored in
 * `financial_account_categories.category_name`, compared case-insensitively.
 */
const DEBIT_NORMAL_CATEGORIES = new Set(["assets", "expenses"]);
/**
 * An account's net balance signed by its CATEGORY's normal balance — the one
 * rule every report total uses (balance sheet, income statement, the
 * accounting dashboard). Assets/Expenses: debits − credits; Liabilities,
 * Equity, Revenue: credits − debits.
 *
 * Deliberately NOT the account's own `normal_balance`: a contra account
 * (accumulated depreciation — credit-normal inside Assets; owner draws —
 * debit-normal inside Equity; sales discounts inside Revenue) must come out
 * NEGATIVE here so it is subtracted from its category total. Summing balances
 * signed by each account's own normal balance would ADD it instead. The
 * per-account balance shown on an account's own page (`calculate_account_balances`)
 * is signed by the account's normal balance and is positive for a contra
 * account; never sum those across a category. docs/CHART_OF_ACCOUNTS.md
 * "Contra subtypes".
 */
export function categoryNetBalance(categoryName, totalDebits, totalCredits) {
    return DEBIT_NORMAL_CATEGORIES.has((categoryName ?? "").toLowerCase())
        ? totalDebits - totalCredits
        : totalCredits - totalDebits;
}
/** Merge two multi-currency totals, summing amounts per currency. */
export function mergeMultiCurrencyTotals(a, b) {
    const result = { ...a };
    for (const [currency, amount] of Object.entries(b)) {
        result[currency] = roundCents((result[currency] || 0) + amount);
    }
    return result;
}
/**
 * Assemble a native, per-currency balance sheet from raw account-balance rows.
 *
 * @param rows            Rows from `get_account_balances_as_of` at the as-of date
 *                        (any/empty array OK).
 * @param priorYearsRows  The same RPC's rows as of the day BEFORE the fiscal year
 *                        holding the as-of date starts — only their Revenue and
 *                        Expenses are read, to split the result in equity.
 * @param baseCurrency    Fallback currency for accounts with no `currency` set.
 */
export function buildBalanceSheet(rows, priorYearsRows, baseCurrency = "EUR") {
    // Build accountTotals, applying the CATEGORY's normal-balance sign
    // (categoryNetBalance — a contra account comes out negative and subtracts).
    // Keyed by account_id — account_name is NOT unique per org (two accounts can
    // share a display name), so keying by name would silently drop one,
    // understating totals and breaking the assets = liabilities + equity check.
    const accountTotals = new Map();
    // Currencies present in the raw rows, collected BEFORE the zero-balance drop
    // below — see the `accountCurrencies` doc comment on BalanceSheetData.
    const accountCurrencies = new Set();
    (rows ?? []).forEach((row) => {
        accountCurrencies.add(row.currency || baseCurrency);
        const activity = categoryNetBalance(row.category_name, row.total_debits, row.total_credits);
        accountTotals.set(row.account_id, {
            name: row.account_name,
            // Rounded to cents here, once, like the trial balance: every figure the
            // sheet prints is a cent amount, the totals are sums of those figures,
            // and float residue never prints (or keeps a zero account on the sheet).
            amount: roundCents(row.opening_balance + activity),
            categoryName: row.category_name,
            typeName: row.type_name,
            currency: row.currency || baseCurrency,
        });
    });
    // Categorize accounts based on account types
    const currentAssets = [];
    const nonCurrentAssets = [];
    const currentLiabilities = [];
    const nonCurrentLiabilities = [];
    const equity = [];
    // Define which account types belong to each category
    const currentAssetTypes = ["Bank", "Accounts Receivable", "Other Current Assets"];
    const nonCurrentAssetTypes = ["Fixed Assets", "Investments", "Other Assets"];
    const currentLiabilityTypes = ["Accounts Payable", "Credit Cards", "Other Current Liabilities"];
    const longTermLiabilityTypes = ["Long-term Liabilities"];
    accountTotals.forEach((account, accountId) => {
        if (account.amount !== 0) {
            // Only include accounts with non-zero balances
            const item = {
                id: accountId,
                name: account.name,
                amount: account.amount,
                currency: account.currency,
            };
            if (account.categoryName === "Assets") {
                // Categorize assets based on account type
                if (currentAssetTypes.includes(account.typeName)) {
                    currentAssets.push(item);
                }
                else if (nonCurrentAssetTypes.includes(account.typeName)) {
                    nonCurrentAssets.push(item);
                }
                else {
                    // Default to current assets for unknown asset types
                    currentAssets.push(item);
                }
            }
            else if (account.categoryName === "Liabilities") {
                // Categorize liabilities based on account type
                if (currentLiabilityTypes.includes(account.typeName)) {
                    currentLiabilities.push(item);
                }
                else if (longTermLiabilityTypes.includes(account.typeName)) {
                    nonCurrentLiabilities.push(item);
                }
                else {
                    // Default to current liabilities for unknown liability types
                    currentLiabilities.push(item);
                }
            }
            else if (account.categoryName === "Equity") {
                // All equity accounts
                equity.push(item);
            }
        }
    });
    // The profit-and-loss result, split at the fiscal-year start. Both figures
    // are rounded to cents BEFORE the non-zero check, and the year's result is
    // the rounded cumulative minus the rounded prior, so the two lines always
    // sum to exactly the cumulative result and float residue never prints a
    // spurious 0.00 line.
    const cumulative = resultByCurrency(rows, baseCurrency);
    const priorYears = resultByCurrency(priorYearsRows, baseCurrency);
    const resultLines = {
        prior_result: [],
        year_result: [],
    };
    const resultCurrencies = Array.from(new Set([...Object.keys(cumulative), ...Object.keys(priorYears)])).sort();
    resultCurrencies.forEach((currency) => {
        const prior = roundCents(priorYears[currency] ?? 0);
        const amounts = {
            prior_result: prior,
            year_result: roundCents(roundCents(cumulative[currency] ?? 0) - prior),
        };
        for (const kind of BALANCE_SHEET_RESULT_KINDS) {
            // Deliberately NO `id` — a roll-up of many accounts, not one account.
            if (amounts[kind] !== 0) {
                resultLines[kind].push({
                    kind,
                    name: `${RESULT_LINE_NAMES[kind]} (${currency})`,
                    amount: amounts[kind],
                    currency,
                });
            }
        }
    });
    // Sort by amount descending within each category
    currentAssets.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    nonCurrentAssets.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    currentLiabilities.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    nonCurrentLiabilities.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    equity.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    // The result closes the equity section (the trial balance's rule too): the
    // accounts by magnitude, then the prior years' result, then the year's.
    equity.push(...resultLines.prior_result, ...resultLines.year_result);
    // Helper to sum items by currency. NOTE: intentionally does NOT uppercase the
    // currency key (unlike currency.ts `sumByCurrency`) — the account currency is
    // already canonical here, and uppercasing would silently re-bucket totals.
    const sumItemsByCurrency = (items) => {
        return items.reduce((acc, item) => {
            const currency = item.currency;
            acc[currency] = roundCents((acc[currency] || 0) + item.amount);
            return acc;
        }, {});
    };
    // Calculate totals by currency
    const currentAssetsTotal = sumItemsByCurrency(currentAssets);
    const nonCurrentAssetsTotal = sumItemsByCurrency(nonCurrentAssets);
    const totalAssetsCalc = mergeMultiCurrencyTotals(currentAssetsTotal, nonCurrentAssetsTotal);
    const currentLiabilitiesTotal = sumItemsByCurrency(currentLiabilities);
    const nonCurrentLiabilitiesTotal = sumItemsByCurrency(nonCurrentLiabilities);
    const totalLiabilitiesCalc = mergeMultiCurrencyTotals(currentLiabilitiesTotal, nonCurrentLiabilitiesTotal);
    const totalEquityCalc = sumItemsByCurrency(equity);
    return {
        assets: {
            current: currentAssets,
            nonCurrent: nonCurrentAssets,
        },
        liabilities: {
            current: currentLiabilities,
            nonCurrent: nonCurrentLiabilities,
        },
        equity,
        totals: {
            currentAssets: currentAssetsTotal,
            nonCurrentAssets: nonCurrentAssetsTotal,
            totalAssets: totalAssetsCalc,
            currentLiabilities: currentLiabilitiesTotal,
            nonCurrentLiabilities: nonCurrentLiabilitiesTotal,
            totalLiabilities: totalLiabilitiesCalc,
            totalEquity: totalEquityCalc,
        },
        accountCurrencies: Array.from(accountCurrencies).sort(),
    };
}
/** Round to cents (the trial balance's `roundCents`, inlined to keep this file dependency-free). */
function roundCents(value) {
    return Math.round(value * 100) / 100 + 0;
}
/**
 * Revenue − Expenses per currency over raw `get_account_balances_as_of` rows,
 * each signed by its category's normal balance (`categoryNetBalance`). Equity,
 * asset and liability rows are ignored.
 */
function resultByCurrency(rows, baseCurrency) {
    const result = {};
    (rows ?? []).forEach((row) => {
        const sign = row.category_name === "Revenue" ? 1 : row.category_name === "Expenses" ? -1 : 0;
        if (sign === 0)
            return;
        const currency = row.currency || baseCurrency;
        const balance = row.opening_balance + categoryNetBalance(row.category_name, row.total_debits, row.total_credits);
        result[currency] = (result[currency] ?? 0) + sign * balance;
    });
    return result;
}
//# sourceMappingURL=balanceSheet.js.map